/**
 * @file Lists the fields a `sourceCells` staging would write, with its
 * decisions overlay applied, each paired with its proven cell or fact and its
 * mapping's confidence. The one reading of a staging that routing
 * (`runPdfIntakeSchedule`, before any overlay exists), the lighting checks and
 * the approval gate (`scheduleApprovalBlockers`, on the server and in S2)
 * share. Cells are keyed to the overlay by
 * `sourceCellRows`' `cellKeys`, the same keys the apply and the review grid use.
 * Left out, as the apply leaves them out: a removed or unassigned table, a
 * removed column, a retargeted table's columns the overlay did not set, junk
 * and rejected rows, and a removed header field. A header field the overlay
 * sets is both edited and decided, since the reviewer supplied its value.
 * Header fields come from `fields[].factIndex`, which indexes the facts of
 * every table in order. Each field's cell carries the lighting checks of its
 * effective target's role (`withRoleChecks`, over `sourceCells.roles`), so a
 * remap moves the checks with it. Each field names its `location`, so S2
 * builds its cells and facts from this list and shows every field the gate
 * counts, including header facts of a table no region shows.
 */
import type { ProcessWholeDocument } from './System.Agent.Extraction.types';
import type { StagedScheduleField } from './System.Agent.PdfEvidence.types';
import { sourceCellRows } from './sourceCellRows';
import { withRoleChecks } from './withRoleChecks';

export function stagedScheduleFields(document: ProcessWholeDocument): StagedScheduleField[] {
    const sourceCells = document.source.sourceCells;
    if (!sourceCells) return [];
    const decisions = document.decisions;
    const roles = sourceCells.roles ?? {};

    const tableFields = document.tables.flatMap((plan): StagedScheduleField[] => {
        const groupId = plan.ref.id;
        const table = sourceCells.tables.find((candidate) => candidate.tableId === groupId);
        const tableDecisions = decisions?.tables[groupId];
        if (!table || tableDecisions?.target?.kind === 'remove') return [];
        const retargeted = tableDecisions?.target?.kind === 'set' && tableDecisions.target.tableId !== plan.tableId;
        const { cellKeys } = sourceCellRows(sourceCells.fileSha256, table);
        return table.cells.flatMap((cell, index): StagedScheduleField[] => {
            const { rowIndex, sourceColumn } = cellKeys[index];
            const columnDecision = tableDecisions?.columns[sourceColumn];
            const proposed = retargeted ? undefined : plan.columns.find((column) => column.sourceColumn === sourceColumn);
            const rowSkipped = plan.rows[rowIndex]?.tag === 'junk' || (tableDecisions?.rejectedRows ?? []).includes(rowIndex);
            if (rowSkipped || columnDecision?.kind === 'remove') return [];
            const mapping = columnDecision?.kind === 'set'
                ? { targetField: columnDecision.targetField, mappingConfidence: 1, mappingDecided: true }
                : proposed
                    ? { targetField: proposed.targetField, mappingConfidence: proposed.confidence, mappingDecided: false }
                    : undefined;
            if (!mapping) return [];
            return [{
                cell: withRoleChecks(cell, roles[mapping.targetField]),
                ...mapping,
                mappingKey: `column:${groupId}/${sourceColumn}`,
                edited: tableDecisions?.cellOverrides[rowIndex]?.[sourceColumn] !== undefined,
                location: { kind: 'cell', tableId: groupId, index, rowIndex, sourceColumn },
            }];
        });
    });

    const facts = sourceCells.tables.flatMap((table) => table.facts.map((fact, index) => ({ fact, tableId: table.tableId, index })));
    const headerFields = document.fields.flatMap((plan): StagedScheduleField[] => {
        const read = facts[plan.factIndex];
        const fieldDecision = decisions?.fields[plan.targetField];
        if (!read || fieldDecision?.kind === 'remove') return [];
        const edited = fieldDecision?.kind === 'set';
        return [{
            cell: withRoleChecks(read.fact, roles[plan.targetField]),
            targetField: plan.targetField,
            mappingConfidence: edited ? 1 : plan.confidence,
            mappingKey: `field:${plan.targetField}`,
            mappingDecided: edited,
            edited,
            location: { kind: 'fact', tableId: read.tableId, index: read.index, factIndex: plan.factIndex },
        }];
    });

    return [...tableFields, ...headerFields];
}
